<script lang="ts">
  // Publishes a new version of a family the caller already owns. Revit-only — PickFiles and
  // ExtractFamilyPackage both need a live session — and deliberately smaller than PublishDialog: an
  // update carries no name, category, subcategory, OmniClass, description or tags, because those
  // live on the family and do not change between versions. Just a file, a version number and a LOD.
  //
  // The new version sits alongside the current live one until a moderator decides it (or, for an
  // organisation/private family, is approved immediately) — see PublishService.CommitUpdateAsync.
  // The family itself, and everyone downloading it, is unaffected while that is pending.

  import { transport } from "@/transport";
  import { LOD_LEVELS } from "./categories";
  import { PICKER_TIMEOUT_MS } from "@/transport/types";
  import type { FamilyPackage, PublishResult } from "./types";
  import { t } from "@/i18n/index.svelte";
  import { AppButton, AppDialog, AppInput, AppSelect } from "@/components/ui";

  let { slug, onclose, onpublished }: { slug: string; onclose?: () => void; onpublished?: () => void } = $props();

  type Step = "choose" | "extracting" | "form" | "uploading" | "done";
  let step = $state<Step>("choose");
  let error = $state("");

  let pkg = $state<FamilyPackage | null>(null);
  let result = $state<PublishResult | null>(null);

  let semver = $state("");
  let lod = $state("");

  async function chooseFile() {
    error = "";
    let paths: string[] = [];
    try {
      const res = await transport.invoke<{ paths: string[] }>("PickFiles", { multiple: false }, { timeoutMs: PICKER_TIMEOUT_MS });
      paths = res.paths ?? [];
    } catch (e) {
      error = messageOf(e);
      return;
    }
    if (!paths.length) return; // cancelled

    step = "extracting";
    try {
      const extracted = await transport.invoke<FamilyPackage>("ExtractFamilyPackage", { localPath: paths[0] });
      if (!extracted.ok) {
        error = extracted.error ?? t("publish.extractFailed");
        step = "choose";
        return;
      }
      pkg = extracted;
      step = "form";
    } catch (e) {
      error = messageOf(e);
      step = "choose";
    }
  }

  async function publish() {
    if (!pkg || !semver.trim() || !lod) return;
    error = "";
    step = "uploading";

    try {
      const res = await transport.invoke<PublishResult>("PublishFamilyUpdate", {
        slug,
        localPath: pkg.localPath,
        semver: semver.trim(),
        lod: Number(lod),
        sourceFileName: pkg.sourceFileName,
        authoringLanguage: pkg.suggestedLanguage ?? null,
        unitSystem: pkg.unitSystem,
        revitYear: pkg.revitYear,
        sha256: pkg.sha256,
        parameters: pkg.parameters,
        types: pkg.types,
      });
      result = res;
      if (res.ok) {
        step = "done";
      } else {
        error = res.error ?? t("update.failed");
        step = "form";
      }
    } catch (e) {
      error = messageOf(e);
      step = "form";
    }
  }

  function messageOf(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  const publishDisabledReason = $derived.by(() => {
    if (step === "uploading") return null;
    if (!semver.trim()) return t("update.semverRequired");
    if (!lod) return t("publish.lodRequired");
    return null;
  });
</script>

<AppDialog size="sm" title={t("update.title")} closeLabel={t("publish.close")} onclose={() => onclose?.()}>
  {#if step === "choose"}
    <p class="text-sm text-ink-secondary">{t("update.chooseHint")}</p>
    <AppButton variant="primary" size="lg" block onclick={chooseFile}>
      {t("publish.choose")}
    </AppButton>
  {:else if step === "extracting"}
    <p class="text-sm text-ink-muted py-8 text-center">{t("publish.extracting")}</p>
  {:else if (step === "form" || step === "uploading") && pkg}
    <div class="flex flex-col gap-3">
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted">{t("update.semver")}</span>
        <AppInput bind:value={semver} type="text" placeholder={t("update.semverHint")} />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted">{t("publish.lod")}</span>
        <AppSelect bind:value={lod}>
          <option value="" disabled>—</option>
          {#each LOD_LEVELS as l (l)}
            <option value={String(l)}>LOD {l}</option>
          {/each}
        </AppSelect>
        <span class="text-xs text-ink-muted">{t("publish.lodHint")}</span>
      </label>
    </div>

    <!-- Facts read from the file — not editable, same as PublishDialog. -->
    <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs text-ink-muted">
      <dt>{t("detail.revitYears")}</dt>
      <dd class="tabular-nums text-ink">{pkg.revitYear}</dd>
      <dt>{t("detail.units")}</dt>
      <dd class="text-ink">{pkg.unitSystem ?? "—"}</dd>
      <dt>{t("detail.parameters")}</dt>
      <dd class="text-ink">{pkg.parameters.length}</dd>
      <dt>{t("batch.types", { count: pkg.types.length })}</dt>
      <dd class="text-ink">{pkg.types.length}</dd>
    </dl>

    {#if error}
      <p class="text-xs text-error text-center">{error}</p>
    {/if}

    <span class="contents" title={publishDisabledReason ?? undefined}>
      <AppButton
        variant="primary"
        size="lg"
        block
        disabled={step === "uploading" || !semver.trim() || !lod}
        onclick={publish}
      >
        {step === "uploading" ? t("publish.publishing") : t("update.publish")}
      </AppButton>
    </span>
  {:else if step === "done" && result}
    <div class="py-4 text-center flex flex-col gap-2">
      <p class="text-sm font-medium">
        {result.status === "pending" ? t("update.pending") : t("update.done")}
      </p>
      <div class="flex gap-2 justify-center mt-2">
        <AppButton
          variant="secondary"
          onclick={() => {
            onpublished?.();
            onclose?.();
          }}
        >
          {t("publish.close")}
        </AppButton>
      </div>
    </div>
  {/if}

  {#if error && step === "choose"}
    <p class="text-xs text-error text-center">{error}</p>
  {/if}
</AppDialog>
